import React, { useState, useEffect, useRef } from 'react';
import { Asset } from 'contentful';
import axios from 'axios';
import logger from '@greenlightme/logger';
import glLogo from '@/images/gl-logo-full-white.svg';
import Image from 'next/legacy/image';
import Lottie from 'lottie-react';
import cx from 'classnames';

import { IVideoTransitionHeroFields } from '@/types/contentful';

import { getImageData } from '../../utils/contentful';

import CtaWidget from '../CtaWidget';
import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';
import GlImage from '../Image';
import AdditionalContent from '../AdditionalContent';
import { Layout } from '../Layout';
import { Flexbox } from '../Flexbox';

export function VideoTransitionHero({
  headline,
  videoTransition,
  desktopBackgroundImage,
  mobileBackgroundImage,
  lottieOverlayImage,
  cta,
  privacyPolicyDisclaimer,
  disclaimerColor,
  additionalContent
}: IVideoTransitionHeroFields) {
  const [overlayImageData, setOverlayImageData] = useState<null | Asset>(null);
  const [animate, setAnimate] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (lottieOverlayImage) {
          const overlayResponse = await axios.get(
            lottieOverlayImage.fields.file.url
          );
          setOverlayImageData(overlayResponse.data);
        }
      } catch (err) {
        logger.error('Error fetching lottie animation data', err);
        console.error('Error fetching lottie animation data', err);
      }
    };

    fetchData();
  }, [lottieOverlayImage]);

  return (
    <>
      <div
        className={cx('fixed inset-0 z-20 flex h-dvh w-dvw bg-white', {
          'animate-shrink': animate
        })}
      >
        {!animate && (
          <video
            className="h-full w-full object-cover"
            ref={videoRef}
            src={videoTransition?.fields.file.url}
            onEnded={() => setAnimate(true)}
            autoPlay
            muted
            playsInline
            data-testid="video-hero"
          />
        )}
      </div>

      <div
        className="relative w-screen overflow-hidden"
        data-blockid="video-transition-hero"
        data-testid="video-transition-hero"
        data-e2e="hero"
      >
        <>
          <div className="absolute h-full w-full flex-1 tablet:max-h-[500px] max:max-h-[600px]">
            <GlImage
              {...getImageData(desktopBackgroundImage)}
              loading="eager"
              layout="fill"
              objectFit="cover"
              className="rounded-b-4xl"
              width={undefined}
              height={undefined}
            />
          </div>
          <div className="absolute overflow-hidden tiny:h-full tiny:w-full tablet:flex tablet:h-[900px] desktop:hidden">
            <GlImage
              {...getImageData(mobileBackgroundImage)}
              loading="eager"
              layout="fill"
              objectFit="cover"
              className="rounded-b-3xl"
              width={undefined}
              height={undefined}
            />
          </div>
        </>
        <Layout gutter={{ tablet: 'collapsibleVert' }}>
          <Flexbox
            flexDirection={{ 0: 'column' }}
            margin={[0, 'auto']}
            justifyContent={{ 0: 'center', tablet: 'center' }}
            alignItems={{ 0: 'center', tablet: 'normal' }}
          >
            <div className="relative z-0 w-full desktop:flex">
              <div className="absolute flex w-full justify-center tiny:mt-2 mobile:mt-5 tablet:mt-8 desktop:left-16 desktop:ml-0 desktop:mt-12 desktop:flex desktop:w-full desktop:justify-start">
                <div className="mt-2 tiny:w-40 tablet:w-48">
                  <Image src={glLogo} alt="Greenlight Logo" />
                </div>
              </div>
              <div>
                <div className="block-container block w-1/2 !justify-normal tablet:flex tablet:h-full tablet:flex-col">
                  <div className="flex px-5 align-bottom tiny:mt-12 tablet:px-48 tablet:pb-11 desktop:mt-14 desktop:px-15 desktop:pb-15 max:mt-24">
                    <div className="w-full">
                      <h1 className="relative mb-4 font-bold text-white tiny:mt-3 tiny:text-center mobile:mt-6 mobile:font-bold tablet:mb-6 tablet:mt-12 tablet:w-screen tablet:max-w-[670px] tablet:text-center tablet:text-[58px] desktop:mt-24 desktop:w-full desktop:max-w-[670px] desktop:text-left desktop:text-5xl min-[1270px]:max-w-[550px] max:max-w-[550px]">
                        {headline}
                      </h1>
                      <div className="flex flex-col items-center mobile:items-center desktop:items-start">
                        {cta && (
                          <div className="z-30 w-[340px] mobile:mb-2 tablet:ml-16 desktop:ml-0">
                            <CtaWidget
                              width={'100%'}
                              {...cta?.fields}
                              sysId={cta?.sys?.contentType?.sys?.id}
                              id="video-transition-hero-cta"
                              module="Hero"
                              height={56}
                              isMobile={false}
                              responsive
                            />
                          </div>
                        )}
                        {privacyPolicyDisclaimer && (
                          <div className="mt-2 max-w-[350px] mobile:mx-auto tablet:ml-48 tablet:max-w-[450px] desktop:ml-0 desktop:max-w-[435px]">
                            <PrivacyPolicyDisclaimer
                              privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                              displayPrivacyPolicyIcon={false}
                              color={disclaimerColor ?? 'black'}
                              centerAlignTablet={true}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex tiny:justify-center tablet:top-[200px] tablet:px-[175px] desktop:top-0 desktop:justify-end desktop:px-0">
                <div className="tiny:w-[380px] mobile:ml-0 mobile:w-full mobile:max-w-[450px] tablet:mt-4 tablet:max-h-[900px] tablet:min-w-[400px] tablet:max-w-[500px] desktop:relative desktop:right-12 desktop:mt-[110px] desktop:max-h-[668px] desktop:max-w-[644px] min-[1130px]:mt-[90px] min-[1180px]:mt-[70px] min-[1230px]:mt-[50px] min-[1270px]:mt-[60px] min-[1270px]:max-w-[540px] min-[1330px]:mt-[70px] min-[1380px]:mt-[60px] max:mt-[80px] max:max-w-[650px]">
                  <Lottie
                    animationData={overlayImageData}
                    loop={true}
                    data-testid="overlay-lottie-image"
                  />
                </div>
              </div>
            </div>
            <AdditionalContent additionalContent={additionalContent} />
          </Flexbox>
        </Layout>
      </div>
    </>
  );
}
